import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { FormHelpers } from '../../../screens/utils';
import { uiStateSelectors } from '../../../services/redux/features/uiState.slice';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from '../components/formInputs';
import { LinkedRelationshipFormModel_MX } from './formModel';
import { getInitialValues } from './initialValues';
import { getValidationSchema } from './validationSchema';

type Props = {
  formHelpers: FormHelpers;
  titleTranslationKey: `linkedRelationshipsForm:title:${string}`;
};

/**
 * - Private
 * - PrivateLease
 */
export const LinkedRelationshipsForm_P_PL: React.FC<Props> = ({ formHelpers, titleTranslationKey }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<Partial<LinkedRelationshipFormModel_MX>>>();

  useEffect(() => {
    setValidationSchema(getValidationSchema());
  }, []);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (values: LinkedRelationshipFormModel_MX) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(titleTranslationKey), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent
          onFormInitialValuesChange={onFormInitialValuesChange}
          disabled={isDisabledFormSummary}
          initialFormValue={initialValues}
          formTranslationKey="linkedRelashionshipsForm"
          formHelpers={formHelpers}
        />
      </Formik>
    </FormSection>
  );
};

export const FormContent = (props) => {
  const [inputs] = useState(createFormInputs);
  const { values, submitForm } = useFormikContext<LinkedRelationshipFormModel_MX>();
  const { disabled, formHelpers } = props;
  const { onFormValuesChange } = formHelpers;

  const snCSubmitted = !useSelector(uiStateSelectors.snCSubmittedMock);
  const disableAllFormControls = disabled || snCSubmitted;

  useEffect(() => {
    onFormValuesChange(values);
  }, [values, onFormValuesChange]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset disabled={disableAllFormControls}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/1">{inputs.majorityInCompanies()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        {values.majorityInCompanies === 'yes' && (
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="1/1">{inputs.majorityInCompaniesCompanyName()}</Layout.Item>
            </Layout>
          </Fieldset.Row>
        )}

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/1">{inputs.partnerOfCompanies()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        {values.partnerOfCompanies === 'yes' && (
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="1/1">{inputs.partnerOfCompaniesCompanyName()}</Layout.Item>
            </Layout>
          </Fieldset.Row>
        )}

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/1">{inputs.customerOfVwfs()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        {values.customerOfVwfs === 'yes' && (
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="1/1">{inputs.contactNumberOrVehicleRegistrationNumber()}</Layout.Item>
            </Layout>
          </Fieldset.Row>
        )}
      </Fieldset>

      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers} />
    </Form>
  );
};
